import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, X } from 'lucide-react';
import { StatusBadge } from '@/components/custom/StatusBadge';
import { Button } from '@/components/ui/button';
import { fetchSimilarTickets } from '@/hooks/useTickets';
import type { SimilarTicketItem } from '@uniticket/shared';

interface SimilarTicketsBannerProps {
  subject: string;
  description?: string;
  jvid?: number | null;
  dmpOrderId?: string | null;
  /** BUILD tickets match on their DMP area instead of a JVID. */
  areaId?: string | null;
  ticketType: string;
  orderSubcategory?: string | null;
  onResultsChange?: (results: SimilarTicketItem[]) => void;
}

export function SimilarTicketsBanner({
  subject,
  description,
  jvid,
  dmpOrderId,
  areaId,
  ticketType,
  orderSubcategory,
  onResultsChange,
}: SimilarTicketsBannerProps) {
  const { t } = useTranslation('tickets');
  const [results, setResults] = useState<SimilarTicketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const hasSubject = subject && subject.length >= 3;
    const hasJvid = jvid != null && jvid > 0;
    const hasDmpOrder = !!dmpOrderId;
    const hasArea = !!areaId;
    if (!hasSubject && !hasJvid && !hasDmpOrder && !hasArea) {
      setResults([]);
      onResultsChange?.([]);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetchSimilarTickets({
          subject: subject || '',
          description: description || undefined,
          jvid: jvid ?? undefined,
          dmpOrderId: dmpOrderId ?? undefined,
          areaId: areaId ?? undefined,
          ticketType,
          orderSubcategory: orderSubcategory ?? undefined,
          limit: 5,
        });
        setResults(res.data);
        onResultsChange?.(res.data);
      } catch {
        setResults([]);
        onResultsChange?.([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [subject, description, jvid, dmpOrderId, areaId, ticketType, orderSubcategory]);

  // Render only once there is something to warn about. Showing the amber box while the lookup is
  // still running made it appear and vanish on every keystroke-settled search that found nothing,
  // which reads as a glitch — and this box is a warning, not a progress indicator.
  if (results.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="text-sm font-semibold text-amber-800">
            {loading ? t('similarTickets.loading') : t('similarTickets.bannerTitle')}
          </span>
          {!loading && results.length > 0 && (
            <span className="text-xs font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
              {results.length}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-amber-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && !loading && results.length > 0 && (
        <div className="px-4 pb-3 space-y-2">
          {results.map((r) => (
            <SimilarTicketRow key={r.id} ticket={r} t={t} />
          ))}
          <p className="text-[11px] text-amber-600 pt-1">{t('similarTickets.proceedAnyway')}</p>
        </div>
      )}
    </div>
  );
}

function SimilarTicketRow({ ticket: r, t }: { ticket: SimilarTicketItem; t: (key: string) => string }) {
  return (
    <Link
      to={`/tickets/${r.id}`}
      target="_blank"
      className="flex items-center gap-3 p-2 rounded-lg bg-white border border-amber-100 hover:border-amber-300 transition-colors"
    >
      <span className="text-xs font-mono font-semibold text-brand-rose shrink-0">{r.ticketNumber}</span>
      <span className="text-xs text-slate-700 truncate flex-1">{r.subject}</span>
      <StatusBadge status={r.status} />
      <div className="flex gap-1 shrink-0">
        {r.matchReasons.slice(0, 2).map((reason) => (
          <span key={reason} className="text-[10px] font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
            {t(`similarTickets.matchReasons.${reason}`)}
          </span>
        ))}
      </div>
    </Link>
  );
}

// ── Confirmation dialog shown before ticket creation ─────

interface SimilarTicketsConfirmDialogProps {
  results: SimilarTicketItem[];
  onConfirm: () => void;
  onCancel: () => void;
}

export function SimilarTicketsConfirmDialog({ results, onConfirm, onCancel }: SimilarTicketsConfirmDialogProps) {
  const { t } = useTranslation('tickets');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-amber-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-white" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wide">
              {t('similarTickets.confirmTitle')}
            </h3>
          </div>
          <button onClick={onCancel} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <p className="text-sm text-slate-600">{t('similarTickets.confirmMessage')}</p>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {results.map((r) => (
              <Link
                key={r.id}
                to={`/tickets/${r.id}`}
                target="_blank"
                className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/50 hover:border-amber-300 transition-colors"
              >
                <span className="text-xs font-mono font-semibold text-brand-rose shrink-0">{r.ticketNumber}</span>
                <span className="text-xs text-slate-700 truncate flex-1">{r.subject}</span>
                <StatusBadge status={r.status} />
                <span className="text-[10px] text-amber-600 shrink-0">{t('similarTickets.score', { score: r.score })}</span>
              </Link>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onCancel}>{t('similarTickets.goBack')}</Button>
            <Button onClick={onConfirm} className="bg-amber-500 text-white hover:bg-amber-600">
              {t('similarTickets.createAnyway')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
